import { MessageOutlined } from '@ant-design/icons';
import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  Button,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClientPathwaysSection,
  type ClientPathwaysHandle,
} from '../pathways/ClientPathwaysSection';
import { EnrollPathwayDrawer } from '../pathways/EnrollPathwayDrawer';
import { hubPrintTargetOf, printPathOf } from '../worksheets/printModel';
import { AssignHomeworkDrawer } from './AssignHomeworkDrawer';
import { HomeworkActModal, type HomeworkAct } from './HomeworkActModal';
import { HomeworkCommentsDrawer } from './HomeworkCommentsDrawer';
import { PaperPhotoViewer } from './PaperPhotoViewer';
import {
  CLIENT_HOMEWORK_PAGE_FIELDS,
  HOMEWORK_PAGE_SIZE,
  AWAITING_BADGE_STYLE,
  awaitingMarkerOf,
  cancelReasonLine,
  HUB_FILTERS,
  couldNotBeReached,
  isNotConnected,
  isOpenRow,
  isSelfSet,
  occurrenceEntries,
  occurrenceLabel,
  remindedToday,
  statusChipOf,
  type HubFilter,
  type HomeworkPageAnswer,
  type HomeworkRow,
} from './model';
import { hasGraphQLErrorCode } from '../../utils/graphqlErrors';
import { useHomeworkLabels } from './useHomeworkLabels';

/**
 * E34 · ClientHomework — "Client Hub — Homework" (cc-homework, chapter "Homework — Assign & follow
 * (portal, E34)"): every assignment the practice or the client made, with its state, the evidence a
 * completion carries and the reason for a skip; Edit / Cancel / Reopen / Send a reminder are its
 * sections. Reads `clientHomeworkPage(subject, status, page, pageSize)` (the inline read model — the hub
 * reads its own writes back at once).
 *
 * Behavior lines implemented here:
 *   · the table pages on the SERVER, 50 rows a page, with the table's usual pager (Q-hw-page-2 A); the
 *     order is the server's (open and viewed first, then the finished newest-finished first — Q-hw-page-3 A);
 *   · the filter chips filter ON THE SERVER and return to page 1; each chip's count is the server's `counts`
 *     for the WHOLE client, never the loaded page's (overdue: open or viewed and past due); the default is All;
 *   · after Edit, Cancel, Reopen, Remind or a reply the tab asks again for the same page and chip; while a
 *     page loads the table keeps its rows; a failed page keeps the rows shown, with a retry;
 *   · a self-set row (requester = the client) is visible but has no Edit/Cancel — the client owns it;
 *   · a row whose latest comment is the client's own carries the "awaiting your reply" dot, and the
 *     chip filters to those rows; the row's Comments action opens the thread (block 4 —
 *     `HomeworkCommentsDrawer`), and a therapist's answer clears the dot;
 *   · a tool completed on paper shows "on paper · photo", and the photo opens in a viewer (the
 *     founder's Q-b1b-2 A — `homeworkPaperPhotoUrl`, each refusal with its own line); a TASK or a WORKSHEET
 *     completed with a photo shows "photo" beside done and opens the same viewer, keyed on `paperPhoto`
 *     (Q-paper-1 A, Q-paper-2 A); a tool whose paper is on reads "paper allowed" (Q-paper-3 B);
 *   · an assigned WORKSHEET the client may do on paper has a Print action — the printable sheet of the
 *     version the assignment pinned, no client name on it (Q-paper-2 A);
 *   · a recurring row reads OPEN while an occurrence is open and its Outcomes column lists each
 *     occurrence — "Oct: done · Nov: open", a missed one reads missed (the founder's Q-b1c-6 C) —
 *     each with its own record (Q-b1e-3 A); a done occurrence answered again inside its window reads
 *     "Oct 6: done · repeated 1" (`repeats`, the founder's Q-b1e-6 A) — never a second done, and the
 *     occurrence after it stays open;
 *   · a row whose latest notice went out on no channel reads "could not be reached" (Q-b1d-4 B);
 *   · the reminder button greys out after today's (at most one a day — the server refuses a second);
 *   · a cancelled row stays, greyed ("nothing is deleted").
 * Block 3a: "Enroll in a pathway" opens its drawer (`../pathways/EnrollPathwayDrawer`), and the client's
 * pathways are listed above the homework, each opening its progress (`../pathways/ClientPathwaysSection`);
 * a released step is an ordinary row of this table.
 */
type Props = {
  clientId: string;
  clientName: string;
  clientTimeZone?: string | null;
  locale: 'en' | 'ru';
};

export const ClientHomeworkTab = ({
  clientId,
  clientName,
  clientTimeZone,
  locale,
}: Props) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const labels = useHomeworkLabels();
  const [filter, setFilter] = useState<HubFilter>('all');
  const [page, setPage] = useState(1);
  const [assigning, setAssigning] = useState(false);
  // E34 block 3a — "Enroll in a pathway" (the drawer) and the client's pathways above the homework
  const [enrolling, setEnrolling] = useState(false);
  const pathwaysRef = useRef<ClientPathwaysHandle>(null);
  const [photo, setPhoto] = useState<{
    assignmentId: string;
    fileRef: string;
  } | null>(null);
  const [acting, setActing] = useState<{
    act: HomeworkAct;
    row: HomeworkRow;
  } | null>(null);
  // block 4 — the row whose comment thread is open
  const [thread, setThread] = useState<HomeworkRow | null>(null);

  /**
   * The client's own time zone — a repeat rule runs in it ("the time is set in your client's own time
   * zone", the E05 editor's rule). Read on its own so a row without one can never break the hub: a
   * failed read falls back to the therapist's zone, and the drawer says which zone it used.
   */
  const { data: zoneData } = useCustom<{
    _id: string;
    timezone?: string | null;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'timezone'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: {
      enabled: Boolean(clientId) && !clientTimeZone,
      retry: false,
    },
  });
  const zone = clientTimeZone || zoneData?.data?.timezone || null;

  /**
   * CAN HER APP SHOW HOMEWORK? (the founder's Q-b1d-1′ A, 2026-09-28) — false while the app the
   * client last used is below the homework floor (`appConfig/es.homeworkFloor`). Assigning still
   * records; the tab and the drawer say it will appear once they update. Unknown (loading, failed)
   * says nothing.
   */
  const { data: readyData } = useCustom<{
    _id: string;
    homeworkAppReady?: boolean | null;
    lng?: string | null;
  }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'client',
      fields: ['_id', 'homeworkAppReady', 'lng'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const appReady =
    typeof readyData?.data?.homeworkAppReady === 'boolean'
      ? readyData.data.homeworkAppReady
      : undefined;

  const { data, isFetching, isPreviousData, isError, error, refetch } =
    useCustom<HomeworkPageAnswer>({
      url: '',
      method: 'get',
      errorNotification: false,
      meta: {
        operation: 'clientHomeworkPage',
        fields: CLIENT_HOMEWORK_PAGE_FIELDS,
        variables: {
          subject: { name: 'subject', type: 'ID!', value: clientId },
          status: { name: 'status', type: 'ClientHomeworkChip', value: filter },
          page: { name: 'page', type: 'Int', value: page },
          pageSize: {
            name: 'pageSize',
            type: 'Int',
            value: HOMEWORK_PAGE_SIZE,
          },
        },
      },
      // while the next page loads the table keeps its rows (Q-hw-page-2 A)
      queryOptions: { enabled: Boolean(clientId), keepPreviousData: true },
    });
  // the last page that DID load — a failed page keeps the rows shown, with a retry
  const lastGood = useRef<HomeworkPageAnswer | null>(null);
  if (data?.data) lastGood.current = data.data;
  const answer = data?.data ?? lastGood.current;
  // a page past the end answers the last one (a chip left fewer rows): the pager moves to the page served
  useEffect(() => {
    // (`isPreviousData`: the rows kept while the asked page loads are the OLD page's — not a verdict)
    if (!isPreviousData && data?.data && data.data.page !== page) {
      setPage(data.data.page);
    }
  }, [data, isPreviousData, page]);
  // Q-mt-2 A: known when a drawer opens — the practice's read of this client was refused
  // …or the writes' own question says the client shares nothing with the caller (clientHomeworkAccess)
  const { data: accessData } = useCustom<{ canAssign: boolean }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientHomeworkAccess',
      fields: ['canAssign'],
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const notConnected = isNotConnected(
    isError && hasGraphQLErrorCode(error, 'HOMEWORK_NOT_YOUR_CLIENT'),
    accessData?.data?.canAssign,
  );
  const shown = useMemo(() => answer?.rows ?? [], [answer]);
  const pickFilter = (f: HubFilter) => {
    setFilter(f);
    setPage(1); // a chip returns to page 1
  };

  const dateLocale = locale === 'ru' ? ru : enUS;
  const when = (iso: string | null | undefined, withTime = true) =>
    iso
      ? format(parseISO(iso), withTime ? 'd MMM yyyy, HH:mm' : 'd MMM yyyy', {
          locale: dateLocale,
        })
      : '—';

  const actButton = (
    row: HomeworkRow,
    act: HomeworkAct,
    disabledWhy?: string,
  ) => {
    const button = (
      <Button
        size="small"
        type="link"
        danger={act === 'cancel'}
        disabled={!!disabledWhy}
        onClick={() => setActing({ act, row })}
        data-testid={`homework-${act}-${row.assignmentId}`}
      >
        {t(`homework.${act}.action`)}
      </Button>
    );
    return disabledWhy ? (
      <Tooltip key={act} title={disabledWhy}>
        <span data-testid={`homework-${act}-${row.assignmentId}-why`}>
          {button}
        </span>
      </Tooltip>
    ) : (
      <span key={act}>{button}</span>
    );
  };

  const actionsOf = (row: HomeworkRow) => {
    // block 4 — the thread under the row, both ways; a withdrawn row's stays readable
    const commentsButton = (
      <span key="comments">
        <Button
          size="small"
          type="link"
          onClick={() => setThread(row)}
          data-testid={`homework-comments-open-${row.assignmentId}`}
        >
          {t('homework.comments.hub.action')}
        </Button>
      </span>
    );
    if (isSelfSet(row)) {
      // Q-board-3 A: the hub shows a self-set row only to a therapist who may view the client's work, and that
      // therapist may reply in its thread — the one act; Edit, Cancel, Remind and Reopen stay the client's
      return (
        <Space direction="vertical" size={0}>
          {commentsButton}
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12 }}
            data-testid={`homework-readonly-${row.assignmentId}`}
          >
            {t('homework.hub.selfReadOnly')}
          </Typography.Text>
        </Space>
      );
    }
    const recurring = !!row.schedule;
    const live = row.status !== 'cancelled' && (isOpenRow(row) || recurring);
    const acts: JSX.Element[] = [];
    acts.push(commentsButton);
    if (live) {
      acts.push(actButton(row, 'edit'));
      acts.push(actButton(row, 'cancel'));
      acts.push(
        actButton(
          row,
          'remind',
          remindedToday(row.lastRemindedAt)
            ? t('homework.remind.sentToday')
            : undefined,
        ),
      );
    }
    // Q-paper-2 A: an assigned worksheet the client may do on paper — the printable sheet, at the version it pinned
    const printTarget = hubPrintTargetOf(row);
    if (printTarget)
      acts.push(
        <span key="print">
          <Button
            size="small"
            type="link"
            onClick={() => navigate(printPathOf(printTarget.templateId, printTarget.version))}
            data-testid={`homework-print-${row.assignmentId}`}
          >
            {t('homework.hub.printWorksheet')}
          </Button>
        </span>,
      );
    if (!isOpenRow(row)) acts.push(actButton(row, 'reopen'));
    return (
      <Space size={0} wrap>
        {acts}
      </Space>
    );
  };

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="homework-hub"
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('homework.hub.title')}
        </Typography.Title>
        <Space wrap>
          <Button
            onClick={() => setEnrolling(true)}
            data-testid="pathway-enroll-open"
          >
            + {t('pathways.hub.enroll')}
          </Button>
          <Button
            type="primary"
            onClick={() => setAssigning(true)}
            data-testid="homework-assign-open"
          >
            + {t('homework.hub.assign')}
          </Button>
        </Space>
      </Space>
      <ClientPathwaysSection
        ref={pathwaysRef}
        clientId={clientId}
        locale={locale}
      />
      <div>
        <Radio.Group
          optionType="button"
          value={filter}
          onChange={(e) => pickFilter(e.target.value)}
          data-testid="homework-filter"
        >
          {HUB_FILTERS.map((f) => (
            <Radio.Button
              key={f}
              value={f}
              data-testid={`homework-filter-${f}`}
            >
              {t(`homework.hub.filter.${f}`)}
              {f === 'all' || !answer ? '' : ` · ${answer.counts[f]}`}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {appReady === false ? (
        <Alert
          type="info"
          showIcon
          message={t('homework.hub.appNotReady', { name: clientName })}
          data-testid="homework-app-not-ready"
        />
      ) : null}

      {isError ? (
        <Alert
          type="error"
          showIcon
          message={
            answer ? t('homework.hub.pageLoadFailed') : t('homework.hub.loadFailed')
          }
          action={
            <Button
              size="small"
              onClick={() => void refetch()}
              data-testid="homework-page-retry"
            >
              {t('homework.hub.retry')}
            </Button>
          }
          data-testid="homework-load-failed"
        />
      ) : null}

      {/* the server's total, OUTSIDE antd's pager: antd draws no pager for a total of 0, so an empty chip kept the last
          count on screen (G2 run, 2026-10-04) */}
      <Typography.Text type="secondary" data-testid="homework-page-total">
        {t('homework.hub.pageTotal', { total: answer?.total ?? 0 })}
      </Typography.Text>
      <Table<HomeworkRow>
        rowKey="assignmentId"
        size="middle"
        loading={isFetching}
        dataSource={shown}
        pagination={{
          // Q-hw-page-2 A: the SERVER pages — the table only shows the page it was given
          current: answer?.page ?? page,
          pageSize: HOMEWORK_PAGE_SIZE,
          total: answer?.total ?? 0,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          hideOnSinglePage: false,
        }}
        tableLayout="fixed"
        scroll={{ x: 900 }}
        locale={{ emptyText: t('homework.hub.empty') }}
        data-testid="homework-table"
        onRow={(row) =>
          ({
            'data-testid': `homework-row-${row.assignmentId}`,
            'data-status': row.status,
            'style': row.status === 'cancelled' ? { opacity: 0.55 } : undefined,
          } as never)
        }
        columns={[
          {
            // the assignment: its title, kind · target and who set it
            title: t('homework.hub.column.homework'),
            width: 280,
            render: (_, row) => (
              <Space direction="vertical" size={0} style={{ maxWidth: 280 }}>
                <Space size={6} align="start">
                  <Tooltip title={row.instructions || undefined}>
                    <Typography.Text strong>{row.title}</Typography.Text>
                  </Tooltip>
                </Space>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  data-testid={`homework-kind-${row.assignmentId}`}
                >
                  {labels.kindTarget(row.kind, row.target)}
                </Typography.Text>
                {row.kind === 'tool' && row.paperAllowed ? (
                  // Q-paper-3 B: the therapist's switch, on — the Edit section changes it
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                    data-testid={`homework-paper-allowed-${row.assignmentId}`}
                  >
                    {t('homework.hub.paperAllowed')}
                  </Typography.Text>
                ) : null}
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  data-testid={`homework-requester-${row.assignmentId}`}
                >
                  {isSelfSet(row)
                    ? t('homework.hub.self', {
                        name: row.requesterName || clientName,
                      })
                    : t('homework.hub.assignedBy', {
                        name: row.requesterName || '—',
                      })}
                </Typography.Text>
              </Space>
            ),
          },
          {
            // one-off: its due instant; recurring: "repeats" and the next occurrence
            title: t('homework.hub.column.due'),
            width: 140,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <span data-testid={`homework-due-${row.assignmentId}`}>
                  {row.schedule ? (
                    <Tag>{t('homework.hub.repeats')}</Tag>
                  ) : (
                    when(row.dueAt)
                  )}
                </span>
                {row.schedule && row.status !== 'cancelled' ? (
                  <Typography.Text style={{ fontSize: 12 }}>
                    {t('homework.hub.nextDue')}{' '}
                    <span data-testid={`homework-next-${row.assignmentId}`}>
                      {when(row.nextDueAt)}
                    </span>
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('homework.hub.column.status'),
            width: 150,
            render: (_, row) => {
              const chip = statusChipOf(row);
              return (
                <Space direction="vertical" size={2}>
                  <Tag
                    color={chip.color}
                    data-testid={`homework-status-${row.assignmentId}`}
                  >
                    {t(`homework.status.${chip.key}`)}
                  </Tag>
                  {awaitingMarkerOf(row) === 'awaiting' ? (
                    // Q-mt-1 A: a labelled badge, never a dot — clicking it opens the thread.
                    // Q-badge-1 A: soft amber (AA contrast), the colours from styles/colors.ts
                    <Tag
                      icon={<MessageOutlined />}
                      style={{ ...AWAITING_BADGE_STYLE, cursor: 'pointer' }}
                      onClick={() => setThread(row)}
                      data-testid={`homework-awaiting-${row.assignmentId}`}
                    >
                      {t('homework.hub.awaitingReply')}
                    </Tag>
                  ) : null}
                  {awaitingMarkerOf(row) === 'noLongerRelevant' ? (
                    // Q-board-1 A: a withdrawn row whose latest comment is the client's — never amber, never counted
                    <Tag
                      icon={<MessageOutlined />}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setThread(row)}
                      data-testid={`homework-no-longer-relevant-${row.assignmentId}`}
                    >
                      {t('homework.hub.noLongerRelevant')}
                    </Tag>
                  ) : null}
                  {row.status === 'cancelled' && row.reason ? (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12 }}
                      data-testid={`homework-cancel-reason-${row.assignmentId}`}
                    >
                      {(() => {
                        const line = cancelReasonLine(row.reason);
                        return line ? t(line.key, line.params) : row.reason;
                      })()}
                    </Typography.Text>
                  ) : null}
                  {couldNotBeReached(row) ? (
                    <Typography.Text
                      type="danger"
                      style={{ fontSize: 12 }}
                      data-testid={`homework-not-reached-${row.assignmentId}`}
                    >
                      {t('homework.hub.notReached')}
                    </Typography.Text>
                  ) : null}
                  {row.lastRemindedAt ? (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12 }}
                      data-testid={`homework-reminded-${row.assignmentId}`}
                    >
                      {t('homework.hub.remindedAt', {
                        date: when(row.lastRemindedAt),
                      })}
                    </Typography.Text>
                  ) : null}
                </Space>
              );
            },
          },
          {
            // what the client did: completed at + evidence + note, or the skip reason
            title: t('homework.hub.column.outcome'),
            width: 190,
            render: (_, row) =>
              row.schedule ? (
                // a recurring row: each occurrence's outcome, each with its own record
                <Space
                  direction="vertical"
                  size={2}
                  data-testid={`homework-occurrences-${row.assignmentId}`}
                >
                  {occurrenceEntries(row).length === 0 ? '—' : null}
                  {occurrenceEntries(row).map((o) => (
                    <div
                      key={`${o.occurrenceAt}-${o.outcome}`}
                      data-testid={`homework-occurrence-${row.assignmentId}-${o.outcome}`}
                    >
                      <span>
                        {t('homework.outcome.line', {
                          when: o.occurrenceAt
                            ? occurrenceLabel(
                                o.occurrenceAt,
                                row.schedule?.rrule,
                                row.schedule?.tzid,
                                locale,
                              )
                            : '—',
                          what: t(`homework.outcome.${o.outcome}`),
                        })}
                      </span>
                      {o.repeats ? (
                        // Q-b1e-6 A: answered again inside its window — never a second done
                        <span
                          data-testid={`homework-occurrence-repeats-${row.assignmentId}`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {' · '}
                          {t('homework.hub.outcomeRepeats', {
                            count: o.repeats,
                          })}
                        </span>
                      ) : null}
                      {o.outcome === 'done' ? (
                        <div>
                          {labels.evidence(o.evidence, {
                            onOpenPhoto: (fileRef) =>
                              setPhoto({
                                assignmentId: row.assignmentId,
                                fileRef,
                              }),
                          })}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </Space>
              ) : row.completedAt ? (
                <Space
                  direction="vertical"
                  size={0}
                  data-testid={`homework-completed-${row.assignmentId}`}
                >
                  <span>{when(row.completedAt)}</span>
                  {labels.evidence(row.evidence, {
                    onOpenPhoto: (fileRef) =>
                      setPhoto({ assignmentId: row.assignmentId, fileRef }),
                  })}
                  {row.paperPhoto && row.evidence?.focusType !== 'paperPhoto' ? (
                    // Q-paper-1 A: a task (the client's word) completed with a photo — "photo" beside done,
                    // the same viewer as a tool's; keyed on `paperPhoto`
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontSize: 12 }}
                      onClick={() =>
                        setPhoto({
                          assignmentId: row.assignmentId,
                          fileRef: (row.paperPhoto as { fileRef: string }).fileRef,
                        })
                      }
                      data-testid={`homework-paper-photo-${row.assignmentId}`}
                    >
                      {t('homework.hub.paperPhoto')}
                    </Button>
                  ) : null}
                  {row.note ? (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12 }}
                      data-testid={`homework-note-${row.assignmentId}`}
                    >
                      {t('homework.hub.note', { note: row.note })}
                    </Typography.Text>
                  ) : null}
                </Space>
              ) : row.reasonCode ? (
                <span data-testid={`homework-skip-${row.assignmentId}`}>
                  {t(`homework.skipReason.${row.reasonCode}`)}
                  {row.reason ? ` — “${row.reason}”` : ''}
                </span>
              ) : (
                '—'
              ),
          },
          {
            title: t('homework.hub.column.actions'),
            width: 200,
            render: (_, row) => actionsOf(row),
          },
        ]}
      />

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('homework.hub.legend')}
      </Typography.Text>

      <AssignHomeworkDrawer
        open={assigning}
        clients={[
          {
            subject: clientId,
            name: clientName,
            appReady,
            notConnected,
            lng: readyData?.data?.lng,
          },
        ]}
        clientTimeZone={zone}
        onClose={() => setAssigning(false)}
        onAssigned={() => refetch()}
      />
      <EnrollPathwayDrawer
        open={enrolling}
        subject={clientId}
        clientName={clientName}
        appReady={appReady}
        notConnected={notConnected}
        onClose={() => setEnrolling(false)}
        onEnrolled={() => {
          pathwaysRef.current?.refetch();
          // the gear releases the first steps on its next tick — the rows appear on the next read
          void refetch();
        }}
      />
      <PaperPhotoViewer photo={photo} onClose={() => setPhoto(null)} />
      <HomeworkCommentsDrawer
        row={thread}
        clientName={clientName}
        locale={locale}
        onClose={() => setThread(null)}
        onChanged={() => refetch()}
      />
      <HomeworkActModal
        act={acting?.act ?? null}
        row={acting?.row ?? null}
        notConnected={notConnected}
        onClose={() => setActing(null)}
        onDone={() => refetch()}
      />
    </Space>
  );
};
